import { useState, useRef, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import imageCompression from 'browser-image-compression';
import { taskApi } from '../../api/client';
import { useApiQuery } from '../../hooks/useApiQuery';
import { useAuthStore } from '../../stores/authStore';
import { Card, CardBody, CardHeader, Badge, Button, Modal, ReviewTypeLogo, AuthenticatedImage, ImageGalleryModal, ErrorAlert, SkeletonTaskDetail, ProgressStepper, SuccessAnimation } from '../../components/ui';
import { TASK_STATUS, PARTICIPATION_STATUS } from '../../lib/constants';
import type { LucideIcon } from 'lucide-react';
import {
  Calendar,
  Users,
  ExternalLink,
  User,
  Upload,
  Clock,
  ArrowLeft,
  List,
  MessageSquare,
  ChevronDown,
  ChevronUp,
  FileText,
  Image,
  X,
  Copy,
  BookOpen,
  AlertTriangle,
  Hand,
  Download,
  Mail,
} from 'lucide-react';

function maskEmail(email: string): string {
  const [local, domain] = email.split('@');
  if (!domain) return email;
  if (local.length <= 2) return `${local[0]}*@${domain}`;
  return `${local.slice(0, 2)}${'*'.repeat(Math.max(local.length - 2, 3))}@${domain}`;
}

function InfoRow({ icon: Icon, label, value }: { icon: LucideIcon; label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4 text-gray-400" />
        <span className="text-sm text-gray-500">{label}</span>
      </div>
      <span className="text-sm font-semibold text-gray-900">{value}</span>
    </div>
  );
}

export function TaskDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isAuthenticated } = useAuthStore();
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [showClaimWarning, setShowClaimWarning] = useState(false);
  const [submissionText, setSubmissionText] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [selectedTextOptionId, setSelectedTextOptionId] = useState<number | null>(null);
  const [claimError, setClaimError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('Task Claimed!');
  const [redirectAfterSuccess, setRedirectAfterSuccess] = useState<string | null>(null);
  const [expandedParticipants, setExpandedParticipants] = useState<Set<number>>(new Set());
  const [filePreviewUrl, setFilePreviewUrl] = useState<string | null>(null);
  const [isCompressing, setIsCompressing] = useState(false);
  const [existingFile, setExistingFile] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Image gallery state
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryImages, setGalleryImages] = useState<string[]>([]);
  const [galleryIndex, setGalleryIndex] = useState(0);

  const openGallery = (images: string[], index: number) => {
    setGalleryImages(images);
    setGalleryIndex(index);
    setGalleryOpen(true);
  };

  // Paste image from clipboard (e.g. screenshot via Ctrl+V)
  const handlePaste = useCallback(async (e: ClipboardEvent) => {
    if (!showSubmitModal) return;
    const items = e.clipboardData?.items;
    if (!items) return;

    for (const item of Array.from(items)) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const pastedFile = item.getAsFile();
        if (!pastedFile) return;

        setExistingFile(null);
        try {
          setIsCompressing(true);
          const compressedFile = await imageCompression(pastedFile, {
            maxSizeMB: 1,
            maxWidthOrHeight: 1920,
            useWebWorker: true,
          });
          setFile(compressedFile);
          setFilePreviewUrl(URL.createObjectURL(compressedFile));
          toast.success(`Pasted image compressed: ${(pastedFile.size / 1024).toFixed(0)}KB → ${(compressedFile.size / 1024).toFixed(0)}KB`);
        } catch {
          setFile(pastedFile);
          setFilePreviewUrl(URL.createObjectURL(pastedFile));
          toast.success('Image pasted from clipboard');
        } finally {
          setIsCompressing(false);
        }
        return;
      }
    }
  }, [showSubmitModal]);

  useEffect(() => {
    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [handlePaste]);

  const { data, isLoading, isError, error, refetch } = useApiQuery({
    queryKey: ['task', id],
    queryFn: () => taskApi.get(parseInt(id!)),
    enabled: !!id,
    refetchOnMount: 'always',
  });

  const claimMutation = useMutation({
    mutationFn: () => taskApi.claim(parseInt(id!), selectedTextOptionId || undefined, selectedGoogleAccountId || undefined),
    onSuccess: (response) => {
      if (response.error) {
        setClaimError(response.error);
        toast.error(response.error);
        return;
      }
      queryClient.invalidateQueries({ queryKey: ['task', id] });
      queryClient.invalidateQueries({ queryKey: ['my-tasks'] });
      setClaimError(null);
      setSuccessMessage('Task Claimed!');
      setRedirectAfterSuccess(null);
      setShowSuccess(true);
      setSelectedGoogleAccountId(null);
      setSelectedTextOptionId(null);
      toast.success('Task claimed successfully');
    },
  });

  const submitMutation = useMutation({
    mutationFn: async () => {
      const formData = new FormData();
      formData.append('submission_text', submissionText);
      if (activeParticipationId) {
        formData.append('participation_id', String(activeParticipationId));
      }
      if (file) {
        formData.append('file', file);
      }
      const activeP = userParticipations.find(p => p.id === activeParticipationId) || participation;
      if (!file && !existingFile && activeP?.submission_file) {
        formData.append('remove_file', 'true');
      }
      return taskApi.submit(parseInt(id!), formData);
    },
    onSuccess: (response) => {
      if (response.error) {
        toast.error(response.error);
        return;
      }
      setShowSubmitModal(false);
      setSubmissionText('');
      setFile(null);
      setExistingFile(null);
      if (filePreviewUrl) {
        URL.revokeObjectURL(filePreviewUrl);
        setFilePreviewUrl(null);
      }
      queryClient.invalidateQueries({ queryKey: ['task', id] });
      queryClient.invalidateQueries({ queryKey: ['my-tasks'] });
      setSuccessMessage('Submitted! Pending Approval');
      setRedirectAfterSuccess('/my-tasks');
      setShowSuccess(true);
    },
    onError: () => {
      toast.error('Failed to submit. Please check your connection and try again.');
    },
  });

  const [selectedGoogleAccountId, setSelectedGoogleAccountId] = useState<number | null>(null);
  const [activeParticipationId, setActiveParticipationId] = useState<number | null>(null);

  const task = data;
  const participation = task?.user_participation;
  const userParticipations = task?.user_participations || [];
  const textOptions = task?.text_options || [];
  const availableTextOptions = textOptions.filter(opt => !opt.is_claimed);
  const hasTextOptions = textOptions.length > 0;
  const textOptionsRequired = task?.text_options_required && hasTextOptions;
  const isGoogleReview = task?.review_type === 'google_review';

  const maxSlots = textOptionsRequired ? textOptions.length : (task?.max_participants || 0);
  const slotsAvailable =
    task && task.participant_count < maxSlots && task.status === 'active';

  // For google_review: can claim more if there are available accounts
  const availableGoogleAccounts = task?.claim_cooldown?.available_accounts || [];
  const canClaimMore = isGoogleReview && availableGoogleAccounts.length > 0 && slotsAvailable;

  // Auto-select if there's only 1 available Google account
  useEffect(() => {
    if (
      isGoogleReview &&
      availableGoogleAccounts.length === 1 &&
      selectedGoogleAccountId === null
    ) {
      setSelectedGoogleAccountId(availableGoogleAccounts[0].id);
    }
  }, [isGoogleReview, availableGoogleAccounts, selectedGoogleAccountId]);
  const hasAnyParticipation = userParticipations.length > 0;

  if (isLoading) {
    return <SkeletonTaskDetail />;
  }

  if (isError) {
    return <ErrorAlert message={error?.message} onRetry={() => refetch()} />;
  }

  if (!task) {
    return (
      <Card>
        <CardBody className="py-12 text-center">
          <p className="text-gray-500">Task not found.</p>
        </CardBody>
      </Card>
    );
  }

  return (
    <div className="space-y-5">
      {/* Back Link */}
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-primary-600 hover:text-primary-700 text-sm font-medium"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Tasks
      </button>

      {/* Task Header (full width) */}
      <div className="flex flex-col md:flex-row items-start justify-between gap-4">
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <ReviewTypeLogo type={task.review_type || ''} size="md" />
            <h1 className="text-2xl font-bold text-gray-900">{task.name}</h1>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Badge
              variant={
                task.status === 'active' ? 'green'
                  : task.status === 'completed' ? 'blue'
                  : task.status === 'expired' ? 'red'
                  : 'gray'
              }
            >
              {TASK_STATUS[task.status]?.label}
            </Badge>
            <span className="text-sm font-semibold text-primary-600">{task.credit_reward} credits reward</span>
            <span className="text-sm text-gray-500">{task.participant_count} of {maxSlots} slots claimed</span>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {task.guide_section_id && (
            <Link
              to={`/guide/${task.guide_section_id}`}
              className="inline-flex items-center px-3 py-2 bg-primary-50 text-primary-600 hover:bg-primary-100 rounded-lg transition-colors text-sm"
            >
              <BookOpen className="w-4 h-4 mr-2" />
              Guide
            </Link>
          )}
        </div>
      </div>

      {/* Cooldown Warning (full width) */}
      {isAuthenticated && slotsAvailable && !participation && task?.claim_cooldown?.blocked && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-4">
          <div className="flex items-start">
            <Clock className="h-5 w-5 text-amber-600 mt-0.5 mr-3 flex-shrink-0" />
            <div>
              {task.claim_cooldown.reason === 'active_claim' ? (
                <p className="text-sm text-amber-800">{task.claim_cooldown.message}</p>
              ) : (
                <div className="text-sm text-amber-800">
                  <p>{task.claim_cooldown.message}</p>
                  <p className="mt-1">
                    You can claim a new task at{' '}
                    <span className="font-semibold">
                      {new Date(task.claim_cooldown.cooldown_ends_at!).toLocaleString()}
                    </span>
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {claimError && <p className="text-sm text-red-600">{claimError}</p>}


      {/* Two-Column Layout */}
      <div className="grid grid-cols-1 md:grid-cols-[1fr_340px] gap-6">
        {/* ===== LEFT COLUMN ===== */}
        <div className="space-y-6 order-2 md:order-1">
          {/* Task Description */}
          <Card>
            <CardHeader>
              <h3 className="text-base font-semibold text-gray-900">Task Description</h3>
            </CardHeader>
            <CardBody className="space-y-4">
              {task.target_url && !participation && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Target URL</p>
                  <a
                    href={task.target_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-sm text-primary-600 font-medium hover:underline break-all"
                  >
                    <ExternalLink className="w-4 h-4 mr-1.5 flex-shrink-0" />
                    {task.target_url}
                  </a>
                </div>
              )}
              {/* Slots Progress */}
              {maxSlots > 0 && (
                <div>
                  <div className="flex items-center justify-between text-sm mb-1.5">
                    <span className="text-gray-500">Slots available</span>
                    <span className="font-semibold text-gray-900">{maxSlots - task.participant_count}/{maxSlots} left</span>
                  </div>
                  <div className="w-full h-2 bg-green-400 rounded-full overflow-hidden relative">
                    <div
                      className="absolute inset-y-0 left-0 bg-red-500 transition-all"
                      style={{ width: `${((task.approved_count || 0) / maxSlots) * 100}%` }}
                    />
                    <div
                      className="absolute inset-y-0 bg-amber-500 transition-all"
                      style={{
                        left: `${((task.approved_count || 0) / maxSlots) * 100}%`,
                        width: `${((task.pending_count || 0) / maxSlots) * 100}%`,
                      }}
                    />
                  </div>
                </div>
              )}
            </CardBody>
          </Card>

          {/* Instructions */}
          {task.instructions && (
            <Card>
              <CardHeader>
                <h3 className="text-base font-semibold text-gray-900">Instructions</h3>
              </CardHeader>
              <CardBody>
                <div className="space-y-2.5">
                  {task.instructions.split('\n').filter(Boolean).map((line, i) => (
                    <div
                      key={i}
                      className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100 hover:border-primary-200 transition-colors"
                    >
                      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-primary-50 text-primary-700 flex items-center justify-center text-sm font-bold">
                        {i + 1}
                      </div>
                      <p className="text-sm text-gray-700 pt-1 leading-relaxed">{line}</p>
                    </div>
                  ))}
                </div>
              </CardBody>
            </Card>
          )}

          {/* Google Account Selector for Google Review tasks */}
          {isGoogleReview && isAuthenticated && (canClaimMore || (!hasAnyParticipation && slotsAvailable)) && (
            <Card>
              <CardBody className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Mail className="w-5 h-5 text-primary-600" />
                  <h3 className="font-semibold text-gray-900">Select Google Account</h3>
                </div>
                {availableGoogleAccounts.length === 0 && !task?.claim_cooldown?.blocked_accounts?.length ? (
                  <div className="text-sm text-gray-500">
                    <p>You need to link a Google account to claim this task.</p>
                    <Link to="/profile" className="text-primary-600 hover:underline font-medium">
                      Go to Profile → Add Google Account
                    </Link>
                  </div>
                ) : (
                  <>
                    <div className="space-y-2">
                      {availableGoogleAccounts.map((account) => (
                        <label
                          key={account.id}
                          className={`flex items-center space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                            selectedGoogleAccountId === account.id
                              ? 'bg-primary-50 border-primary-300'
                              : 'bg-white border-gray-200 hover:border-primary-200'
                          }`}
                        >
                          <input
                            type="radio"
                            name="google_account"
                            value={account.id}
                            checked={selectedGoogleAccountId === account.id}
                            onChange={() => setSelectedGoogleAccountId(account.id)}
                            className="h-4 w-4 text-primary-600"
                          />
                          <div>
                            <p className="text-sm font-medium text-gray-900">{account.google_email}</p>
                            {account.google_name && <p className="text-xs text-gray-500">{account.google_name}</p>}
                          </div>
                        </label>
                      ))}
                      {task?.claim_cooldown?.blocked_accounts?.map((account) => (
                        <div
                          key={account.id}
                          className="flex items-center space-x-3 p-3 rounded-lg border border-gray-200 bg-gray-50 opacity-60"
                        >
                          <input type="radio" disabled className="h-4 w-4" />
                          <div>
                            <p className="text-sm font-medium text-gray-500">{account.google_email}</p>
                            <p className="text-xs text-gray-400">
                              {account.reason === 'already_claimed' ? 'Already claimed' :
                               account.reason === 'active_on_other_task' ? 'Active on another task' :
                               account.reason === 'cooldown' ? 'Cooldown active' : account.reason}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                    {!hasTextOptions && (
                      <Button
                        onClick={() => {
                          setClaimError(null);
                          if (!selectedGoogleAccountId) {
                            setClaimError('Please select a Google account');
                            return;
                          }
                          setShowClaimWarning(true);
                        }}
                        loading={claimMutation.isPending}
                        disabled={!selectedGoogleAccountId}
                        className="w-full"
                      >
                        Claim Slot
                      </Button>
                    )}
                  </>
                )}
              </CardBody>
            </Card>
          )}

          {/* Claim button (no text options, non-google) */}
          {!isGoogleReview && !hasTextOptions && isAuthenticated && slotsAvailable && !participation && !(task?.claim_cooldown?.blocked) && (
            <Button
              onClick={() => {
                setClaimError(null);
                setShowClaimWarning(true);
              }}
              loading={claimMutation.isPending}
              className="w-full"
            >
              Claim Slot
            </Button>
          )}

          {/* Text Options Selection */}
          {hasTextOptions && isAuthenticated && slotsAvailable && (!participation || canClaimMore) && (
            <Card>
              <CardBody className="space-y-3">
                <div className="flex items-center space-x-2">
                  <List className="w-5 h-5 text-primary-600" />
                  <h3 className="font-semibold text-gray-900">
                    Select Your Text Option
                    {textOptionsRequired && <span className="text-red-500 ml-1">*</span>}
                  </h3>
                </div>
                <p className="text-sm text-gray-600">
                  {textOptionsRequired
                    ? 'You must select one of the available text options to claim this task.'
                    : 'You can optionally select a text option for this task.'}
                </p>
                <div className="space-y-2">
                  {textOptions.map((option, index) => (
                    <label
                      key={option.id}
                      className={`flex items-start space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        option.is_claimed
                          ? 'bg-gray-100 border-gray-200 cursor-not-allowed opacity-60'
                          : selectedTextOptionId === option.id
                          ? 'bg-primary-50 border-primary-300'
                          : 'bg-white border-gray-200 hover:border-primary-200'
                      }`}
                    >
                      <input
                        type="radio"
                        name="text_option"
                        value={option.id}
                        checked={selectedTextOptionId === option.id}
                        disabled={option.is_claimed}
                        onChange={() => setSelectedTextOptionId(option.id)}
                        className="mt-1 h-4 w-4 text-primary-600"
                      />
                      <div className="flex-1 flex flex-col md:flex-row gap-2">
                        <div className="flex-1 flex flex-row gap-2">
                          <div className="ml-2 font-medium text-gray-500">{index + 1}.</div>
                          <div className="text-gray-700">{option.text_content}</div>
                        </div>
                        {option.image_urls && option.image_urls.length > 0 && (
                          <div className="flex flex-wrap gap-2 justify-end">
                            {option.image_urls.map((url, imgIndex) => (
                              <div
                                key={imgIndex}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  openGallery(option.image_urls!, imgIndex);
                                }}
                              >
                                <AuthenticatedImage
                                  src={url}
                                  alt={`Option ${index + 1} image ${imgIndex + 1}`}
                                  className="w-20 h-20 object-cover rounded border cursor-pointer hover:opacity-80"
                                />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      {option.is_claimed && (
                        <Badge variant="gray">Taken</Badge>
                      )}
                    </label>
                  ))}
                </div>
                {availableTextOptions.length === 0 && (
                  <p className="text-sm text-red-600">
                    All text options have been claimed. No slots available.
                  </p>
                )}

                {/* Claim button inside text options card */}
                {isAuthenticated && slotsAvailable && (!participation || canClaimMore) && !(task?.claim_cooldown?.blocked && !isGoogleReview) && (
                  <Button
                    onClick={() => {
                      setClaimError(null);
                      if (textOptionsRequired && !selectedTextOptionId) {
                        setClaimError('Please select a text option before claiming this task');
                        return;
                      }
                      if (isGoogleReview && !selectedGoogleAccountId) {
                        setClaimError('Please select a Google account above');
                        return;
                      }
                      setShowClaimWarning(true);
                    }}
                    loading={claimMutation.isPending}
                    disabled={(textOptionsRequired && !selectedTextOptionId) || (isGoogleReview && !selectedGoogleAccountId)}
                    className="w-full"
                  >
                    Claim Slot
                  </Button>
                )}
              </CardBody>
            </Card>
          )}

          {/* Participants */}
          <Card>
            <CardHeader>
              <h2 className="text-lg font-semibold text-gray-900">Participants</h2>
            </CardHeader>
            <CardBody className="!p-0">
              {task.participants.length === 0 ? (
                <p className="p-6 text-gray-500 text-center">No participants yet</p>
              ) : (
                <ul className="divide-y divide-gray-200">
                  {task.participants.map((p, i) => {
                    const isExpanded = expandedParticipants.has(i);
                    const selectedTextOption = p.selected_text_option_id
                      ? textOptions.find(opt => opt.id === p.selected_text_option_id)
                      : null;
                    const hasDetails = selectedTextOption || p.submitted_at;

                    return (
                      <li key={i} className="border-b border-gray-200 last:border-b-0">
                        <button
                          onClick={() => {
                            if (!hasDetails) return;
                            setExpandedParticipants(prev => {
                              const next = new Set(prev);
                              if (next.has(i)) {
                                next.delete(i);
                              } else {
                                next.add(i);
                              }
                              return next;
                            });
                          }}
                          className={`w-full px-4 py-3 flex items-center justify-between ${
                            hasDetails ? 'cursor-pointer hover:bg-gray-50' : 'cursor-default'
                          }`}
                        >
                          <div className="flex items-center space-x-3">
                            <User className="w-5 h-5 text-gray-400" />
                            <div className="text-left">
                              <span className="text-sm text-gray-900">{p.full_name}</span>
                              {p.google_email && (
                                <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                                  <Mail className="w-3 h-3" />
                                  <span className="truncate">{maskEmail(p.google_email)}</span>
                                </p>
                              )}
                            </div>
                            {hasDetails && (
                              isExpanded ? (
                                <ChevronUp className="w-4 h-4 text-gray-400" />
                              ) : (
                                <ChevronDown className="w-4 h-4 text-gray-400" />
                              )
                            )}
                          </div>
                          <Badge
                            variant={
                              p.status === 'approved' ? 'blue'
                                : p.status === 'rejected' ? 'red'
                                : p.status === 'submitted' ? 'yellow'
                                : p.status === 'claimed' ? 'yellow'
                                : 'gray'
                            }
                          >
                            {PARTICIPATION_STATUS[p.status as keyof typeof PARTICIPATION_STATUS]?.label}
                          </Badge>
                        </button>

                        {/* Expanded Details */}
                        <div
                          className={`overflow-hidden transition-all duration-300 ${
                            isExpanded && hasDetails ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                          }`}
                        >
                          <div className="pl-4 pr-2 py-2 bg-gray-50 space-y-2">
                            {selectedTextOption && (
                              <div className="flex items-start gap-2">
                                <List className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
                                <div className="bg-white px-3 py-1 rounded-lg flex-1">
                                  <span className="text-sm text-gray-700">{selectedTextOption.text_content}</span>
                                </div>
                              </div>
                            )}
                            {p.submitted_at && (
                              <div className="flex items-center justify-end gap-2">
                                <Clock className="w-4 h-4 text-gray-500 flex-shrink-0" />
                                <div>
                                  <span className="text-xs text-gray-500">Submitted At</span>&nbsp;
                                  <span className="text-xs text-gray-700">
                                    {new Date(p.submitted_at).toLocaleString()}
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </CardBody>
          </Card>
        </div>

        {/* ===== RIGHT COLUMN (Sidebar) ===== */}
        <div className="space-y-6 order-1 md:order-2">
          {/* Your Progress — renders per participation */}
          {userParticipations.map((p) => {
            const pCanSubmit = p.status === 'claimed' || p.status === 'submitted' || p.status === 'rejected';
            return (
              <Card key={p.id}>
                <CardBody className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="text-base font-semibold text-gray-900">Your Progress</h3>
                      {p.google_email && (
                        <span className="inline-flex items-center text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded">
                          <Mail className="w-3 h-3 mr-1" />
                          {p.google_email}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">
                      {p.status === 'claimed' && 'Complete the task and submit your work'}
                      {p.status === 'submitted' && 'Waiting for admin review'}
                      {p.status === 'approved' && 'Your work has been approved!'}
                      {p.status === 'rejected' && 'Your submission was not approved'}
                    </p>
                  </div>
                  <ProgressStepper status={p.status as 'claimed' | 'submitted' | 'approved' | 'rejected'} />

                  {/* Target URL + Assigned Text/Image grouped together */}
                  {(task.target_url || p.selected_text_option) && (
                    <div className="p-3 bg-gray-50 rounded-lg border border-gray-100 space-y-3">
                      {task.target_url && (
                        <div>
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Target URL</p>
                          <a
                            href={task.target_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center text-sm text-primary-600 font-medium hover:underline break-all"
                          >
                            <ExternalLink className="w-4 h-4 mr-1.5 flex-shrink-0" />
                            {task.target_url}
                          </a>
                        </div>
                      )}
                      {p.selected_text_option && (
                        <>
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <MessageSquare className="w-4 h-4 text-primary-600" />
                              <span className="text-xs font-medium text-gray-700">Your assigned text:</span>
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText(p.selected_text_option!.text_content);
                                  toast.success('Copied to clipboard');
                                }}
                                className="p-1 text-gray-400 hover:text-primary-600 transition-colors"
                                title="Copy to clipboard"
                              >
                                <Copy className="w-3.5 h-3.5" />
                              </button>
                            </div>
                            <p className="text-sm text-gray-900">{p.selected_text_option.text_content}</p>
                          </div>
                          {p.selected_text_option.image_urls && p.selected_text_option.image_urls.length > 0 && (
                            <div className="flex flex-wrap gap-3">
                              {p.selected_text_option.image_urls.map((url, i) => (
                                <div key={i} className="flex flex-col items-center gap-1.5">
                                  <div onClick={() => openGallery(p.selected_text_option!.image_urls!, i)}>
                                    <AuthenticatedImage
                                      src={url}
                                      alt={`Reference image ${i + 1}`}
                                      className="w-40 h-40 object-cover rounded-lg border hover:opacity-80 cursor-pointer"
                                    />
                                  </div>
                                  <button
                                    onClick={async () => {
                                      const token = useAuthStore.getState().token;
                                      if (!token) return;
                                      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
                                      const blob = await res.blob();
                                      const a = document.createElement('a');
                                      a.href = URL.createObjectURL(blob);
                                      a.download = `reference-image-${i + 1}.jpg`;
                                      a.click();
                                      URL.revokeObjectURL(a.href);
                                    }}
                                    className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-800 transition-colors"
                                  >
                                    <Download className="w-3.5 h-3.5" />
                                    Download
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  {/* Review comment */}
                  {p.review_comment && (
                    <div className="border border-amber-300 p-3 rounded-lg bg-amber-50">
                      <p className="text-sm text-amber-800">
                        <strong>Admin note:</strong> {p.review_comment}
                      </p>
                    </div>
                  )}

                  {/* Per-participation submit */}
                  {pCanSubmit && (
                    <button
                      onClick={() => {
                        setActiveParticipationId(p.id);
                        setSubmissionText(p.submission_text || '');
                        setExistingFile(p.submission_file || null);
                        setFile(null);
                        if (filePreviewUrl) {
                          URL.revokeObjectURL(filePreviewUrl);
                          setFilePreviewUrl(null);
                        }
                        setShowSubmitModal(true);
                      }}
                      className="w-full border-2 border-dashed border-primary-300 rounded-xl p-6 flex flex-col items-center gap-2 hover:bg-primary-50/50 transition-colors"
                    >
                      <Upload className="w-8 h-8 text-primary-500" />
                      <span className="text-sm font-semibold text-gray-900">
                        {p.status === 'submitted' ? 'Edit Submission' : 'Upload Proof *'}
                      </span>
                      <span className="text-xs text-gray-400">Choose a file or paste a screenshot (Ctrl+V)</span>
                    </button>
                  )}
                </CardBody>
              </Card>
            );
          })}

          {/* Task Details */}
          <Card>
            <div className="px-5 py-4 border-b border-gray-200">
              <h3 className="text-base font-semibold text-gray-900">Task Details</h3>
            </div>
            <div className="px-5 py-4 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ReviewTypeLogo type={task.review_type || ''} size="xs" />
                  <span className="text-sm text-gray-500">Review Type</span>
                </div>
                <span className="text-sm font-semibold text-gray-900">
                  {task.review_type === 'google_review' ? 'Google Review' : task.review_type === 'facebook_like' ? 'Facebook' : 'Review'}
                </span>
              </div>
              <InfoRow icon={Calendar} label="End Date" value={new Date(task.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} />
              <InfoRow icon={Users} label="Total Slots" value={String(maxSlots)} />
              <InfoRow icon={Hand} label="Claimed" value={String(task.participant_count)} />
              {task.claim_timeout_hours && (
                <InfoRow icon={Clock} label="Claim Timeout" value={`${task.claim_timeout_hours} hours`} />
              )}
            </div>
          </Card>

          {/* Important Notice */}
          <div className="rounded-xl bg-amber-50 border border-amber-200/50 p-4 flex gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="space-y-1.5">
              <p className="text-sm font-semibold text-amber-700">Important Notice</p>
              <p className="text-xs text-amber-600 leading-relaxed">
                Please do not rush your reviews. We strongly suggest completing no more than one review per day. Reviews done too quickly may appear inauthentic and could be flagged or rejected.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Claim Warning Modal */}
      <Modal
        isOpen={showClaimWarning}
        onClose={() => setShowClaimWarning(false)}
        title="Important Reminder"
        size="md"
      >
        <div className="space-y-4">
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-amber-800 font-medium">Please do not rush your reviews.</p>
                <p className="mt-1 text-sm text-amber-700">
                  We strongly suggest completing no more than <span className="font-semibold">one review per day</span>. Reviews done too quickly may appear inauthentic and could be flagged or rejected.
                </p>
              </div>
            </div>
          </div>
          <div className="flex justify-end space-x-3">
            <Button variant="secondary" onClick={() => setShowClaimWarning(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                setShowClaimWarning(false);
                claimMutation.mutate();
              }}
              loading={claimMutation.isPending}
            >
              I Understand, Claim Task
            </Button>
          </div>
        </div>
      </Modal>

      {/* Submit Modal */}
      <Modal
        isOpen={showSubmitModal}
        onClose={() => {
          setShowSubmitModal(false);
          setExistingFile(null);
          if (filePreviewUrl) {
            URL.revokeObjectURL(filePreviewUrl);
            setFilePreviewUrl(null);
          }
        }}
        title={participation?.status === 'submitted' ? 'Edit Submission' : 'Submit Completion'}
        size="lg"
      >
        <div className="space-y-4">
          <div>
            <label className="label">Description / Notes (optional)</label>
            <textarea
              className="input min-h-[100px]"
              value={submissionText}
              onChange={(e) => setSubmissionText(e.target.value)}
              placeholder="Describe your work or add any notes..."
            />
          </div>

          <div>
            <label className="label">Upload Proof *</label>

            {/* Existing File Preview */}
            {existingFile && !file && (
              <div className="mb-3">
                <p className="text-sm text-gray-600 mb-2">Current uploaded file:</p>
                <div className="relative inline-block">
                  <button
                    type="button"
                    onClick={() => {
                      setExistingFile(null);
                      if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                      }
                    }}
                    className="absolute -top-2 -right-2 z-10 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                    title="Remove existing file"
                  >
                    <X className="w-4 h-4" />
                  </button>
                  <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                    {/\.(jpg|jpeg|png|gif|webp)$/i.test(existingFile) ? (
                      <div className="flex flex-col items-center">
                        <Image className="w-5 h-5 text-gray-500 mb-2" />
                        <AuthenticatedImage
                          src={`/api/uploads/${existingFile}`}
                          alt="Current submission"
                          className="max-h-48 max-w-full object-contain rounded"
                        />
                        <p className="mt-2 text-xs text-gray-500">{existingFile}</p>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center">
                        <FileText className="w-8 h-8 text-red-500 mb-2" />
                        <p className="text-sm font-medium text-gray-700">{existingFile}</p>
                      </div>
                    )}
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">Upload a new file below to replace it.</p>
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp,.pdf"
              disabled={isCompressing}
              onChange={async (e) => {
                const selectedFile = e.target.files?.[0] || null;

                if (filePreviewUrl) {
                  URL.revokeObjectURL(filePreviewUrl);
                }

                if (!selectedFile) {
                  setFile(null);
                  setFilePreviewUrl(null);
                  return;
                }

                const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
                if (!allowedTypes.includes(selectedFile.type)) {
                  toast.error('Unsupported file type. Please upload a JPEG, PNG, GIF, WebP, or PDF file.');
                  if (fileInputRef.current) fileInputRef.current.value = '';
                  return;
                }

                const maxSize = 10 * 1024 * 1024;
                if (selectedFile.size > maxSize) {
                  if (!selectedFile.type.startsWith('image/')) {
                    toast.error(`File is too large (${(selectedFile.size / 1024 / 1024).toFixed(1)}MB). Maximum size is 10MB.`);
                    if (fileInputRef.current) fileInputRef.current.value = '';
                    return;
                  }
                }

                setExistingFile(null);

                if (selectedFile.type.startsWith('image/')) {
                  try {
                    setIsCompressing(true);
                    const options = {
                      maxSizeMB: 1,
                      maxWidthOrHeight: 1920,
                      useWebWorker: true,
                    };
                    const compressedFile = await imageCompression(selectedFile, options);
                    if (compressedFile.size > maxSize) {
                      toast.error(`Image is still too large after compression (${(compressedFile.size / 1024 / 1024).toFixed(1)}MB). Please use a smaller image.`);
                      if (fileInputRef.current) fileInputRef.current.value = '';
                      return;
                    }
                    setFile(compressedFile);
                    setFilePreviewUrl(URL.createObjectURL(compressedFile));
                    toast.success(`Image compressed: ${(selectedFile.size / 1024).toFixed(0)}KB → ${(compressedFile.size / 1024).toFixed(0)}KB`);
                  } catch (error) {
                    console.error('Compression failed:', error);
                    if (selectedFile.size > maxSize) {
                      toast.error(`Image compression failed and the original file is too large (${(selectedFile.size / 1024 / 1024).toFixed(1)}MB). Please use a smaller image.`);
                      if (fileInputRef.current) fileInputRef.current.value = '';
                      return;
                    }
                    toast.warning('Image compression failed. Using original file.');
                    setFile(selectedFile);
                    setFilePreviewUrl(URL.createObjectURL(selectedFile));
                  } finally {
                    setIsCompressing(false);
                  }
                } else {
                  setFile(selectedFile);
                  setFilePreviewUrl(URL.createObjectURL(selectedFile));
                }
              }}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100 disabled:opacity-50"
            />
            {isCompressing && (
              <p className="mt-1 text-xs text-primary-600 flex items-center">
                <span className="animate-spin mr-1">⏳</span> Compressing image...
              </p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              Choose a file or paste a screenshot (Ctrl+V). Supported: JPEG, PNG, GIF, WebP, PDF (max 10MB)
            </p>

            {/* New File Preview */}
            {file && filePreviewUrl && (
              <div className="mt-3 relative">
                <button
                  type="button"
                  onClick={() => {
                    setFile(null);
                    if (filePreviewUrl) {
                      URL.revokeObjectURL(filePreviewUrl);
                    }
                    setFilePreviewUrl(null);
                    if (fileInputRef.current) {
                      fileInputRef.current.value = '';
                    }
                  }}
                  className="absolute -top-2 -right-2 z-10 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                >
                  <X className="w-4 h-4" />
                </button>
                <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                  {file.type.startsWith('image/') ? (
                    <div className="flex flex-col items-center">
                      <Image className="w-5 h-5 text-gray-500 mb-2 absolute top-3 left-3" />
                      <img
                        src={filePreviewUrl}
                        alt="Preview"
                        className="max-h-48 max-w-full object-contain rounded"
                      />
                      <p className="mt-2 text-xs text-gray-500">{file.name} ({(file.size / 1024).toFixed(1)} KB)</p>
                    </div>
                  ) : file.type === 'application/pdf' ? (
                    <div className="flex flex-col items-center">
                      <FileText className="w-8 h-8 text-red-500 mb-2 absolute top-3 left-3" />
                      <p className="text-sm font-medium text-gray-700">{file.name}</p>
                      <p className="text-xs text-gray-500">({(file.size / 1024).toFixed(1)} KB)</p>
                      <a
                        href={filePreviewUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-2 text-sm text-primary-600 hover:text-primary-700"
                      >
                        Preview PDF
                      </a>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <FileText className="w-5 h-5 text-gray-500 absolute top-3 left-3" />
                      <span className="text-sm text-gray-700">{file.name}</span>
                      <span className="text-xs text-gray-500">({(file.size / 1024).toFixed(1)} KB)</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
              <p className="text-xs font-medium text-gray-600 mb-2">Example of a valid proof:</p>
              <img
                src="/example-proof.jpg"
                alt="Example completion proof"
                className="rounded-md max-h-60 mx-auto"
              />
            </div>

          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button variant="secondary" onClick={() => {
              setShowSubmitModal(false);
              setExistingFile(null);
              if (filePreviewUrl) {
                URL.revokeObjectURL(filePreviewUrl);
                setFilePreviewUrl(null);
              }
            }}>
              Cancel
            </Button>
            <Button
              variant="success"
              onClick={() => submitMutation.mutate()}
              loading={submitMutation.isPending}
              disabled={!file && !existingFile}
            >
              {participation?.status === 'submitted' ? 'Update Submission' : 'Submit for Review'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Image Gallery Modal */}
      <ImageGalleryModal
        isOpen={galleryOpen}
        onClose={() => setGalleryOpen(false)}
        images={galleryImages}
        initialIndex={galleryIndex}
      />

      <SuccessAnimation
        show={showSuccess}
        message={successMessage}
        onComplete={() => {
          setShowSuccess(false);
          if (redirectAfterSuccess) {
            navigate(redirectAfterSuccess);
            setRedirectAfterSuccess(null);
          }
        }}
      />
    </div>
  );
}
